import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { setUser, clearUser } from '../../store/userSlice';
import LoginService from '../../services/login-service';
import { startActivitySession } from '../../services/activity-session';
import { readSessionStatus, reportUserActivity } from '../../services/session-api';

const ProtectedRoute = ({ children, requiredRole }) => {
    const user = useSelector(state => state.user.user);
    const dispatch = useDispatch();
    const { getUserInfo } = useMemo(() => LoginService(), []);

    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const userInfo = await getUserInfo();
            if (userInfo) {
                dispatch(setUser(userInfo));
            } else {
                dispatch(clearUser());
            }
        } catch (error) {
            console.error("Auth check failed:", error);
            if (error.response?.status === 401) dispatch(clearUser());
        } finally {
            setIsCheckingAuth(false);
        }
    }, [getUserInfo, dispatch]);

    useEffect(() => {
        checkAuth();

        return startActivitySession({
            readStatus: readSessionStatus,
            heartbeat: reportUserActivity,
            onExpired: () => dispatch(clearUser()),
        });
    }, [checkAuth, dispatch]);

    if (isCheckingAuth) {
        return <div>Loading...</div>;
    }

    if (!user) {
        return <Navigate to="/?sessionExpired=true" replace />;
    }

    if (requiredRole) {
        const userRoles = user.roles || [];
        const hasRequiredRole = userRoles.includes(requiredRole) || userRoles.includes(`ROLE_${requiredRole}`);

        if (!hasRequiredRole) {
            return <Navigate to="/after-login" replace />;
        }
    }

    return children;
};

export default ProtectedRoute;
